import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Anchor, ArrowRight, Download, Check, Mail, User, Send, Loader2, PlayCircle, Share2, MessageCircle, Link2, Send as SendIcon, KeyRound, Sparkles, Camera, TrendingDown, Sailboat, FileBarChart, UtensilsCrossed, WifiOff, Crown, Calculator, ChefHat, Quote } from "lucide-react";

const COVER = "/cover-yacht.jpg";

const SHOTS = [
  { src: "/storyboard/01_landing.jpeg", scene: "01 · Apertura", title: "Costruito a bordo, non in ufficio" },
  { src: "/storyboard/03_dashboard.jpeg", scene: "02 · Dashboard", title: "Food cost e budget in tempo reale" },
  { src: "/storyboard/05_scan.jpeg", scene: "03 · Scansione AI", title: "Fotografi la bolla, l'AI compila" },
  { src: "/storyboard/06_prezzi.jpeg", scene: "04 · Confronto Prezzi", title: "Lo stesso prodotto tra fornitori" },
  { src: "/storyboard/04_acquisti.jpeg", scene: "05 · Acquisti", title: "Fatture e scontrini sempre in ordine" },
  { src: "/storyboard/07_coperti.jpeg", scene: "06 · Coperti", title: "Costo reale per coperto" },
  { src: "/storyboard/08_giacenze.jpeg", scene: "07 · Giacenze", title: "Food cost calcolato sul consumo reale" },
  { src: "/storyboard/09_menu.jpeg", scene: "08 · Menù + Allergeni", title: "Allergeni rilevati in automatico" },
  { src: "/storyboard/10_ordini.jpeg", scene: "09 · Ordini", title: "Invio al fornitore in un tocco" },
  { src: "/storyboard/11_charter.jpeg", scene: "10 · Charter", title: "Spesa e margine per ogni charter" },
  { src: "/storyboard/12_report.jpeg", scene: "11 · Report", title: "PDF ed Excel per l'armatore" },
  { src: "/storyboard/13_scadenze.jpeg", scene: "12 · Scadenze HACCP", title: "Avvisi anche ad app chiusa" },
];

const BENEFITS = [
  { icon: Camera, title: "Scansione AI delle fatture", text: "Fotografi bolle e scontrini: l'AI compila prodotti, quantità e prezzi. Basta inserimenti manuali." },
  { icon: TrendingDown, title: "Food cost sotto controllo", text: "Costo per coperto in tempo reale, budget Ospiti e Ciurma separati e avvisi automatici allo sforamento." },
  { icon: Sailboat, title: "Confronto prezzi fornitori", text: "Vedi subito chi costa meno sullo stesso prodotto e risparmi a ogni ordine." },
  { icon: FileBarChart, title: "Report per l'Armatore", text: "PDF ed Excel professionali con food cost, budget e margini, pronti da inviare via email." },
  { icon: UtensilsCrossed, title: "Menù con allergeni", text: "I 14 allergeni UE rilevati in automatico e menù eleganti pronti per gli ospiti." },
  { icon: WifiOff, title: "Offline e avvisi HACCP", text: "Registri le spese anche senza rete e ricevi gli avvisi di scadenza HACCP pure ad app chiusa." },
];

const PLAN_INCLUDES = [
  "Miglioramenti su misura inclusi, senza costi extra",
  "Supporto dedicato, sempre disponibile quando ti serve",
  "Tutte le funzioni: acquisti, coperti, giacenze, food cost",
  "Scansione AI di fatture ed etichette",
  "Report PDF ed Excel per l'Armatore",
  "Menù con allergeni e Charter Planner",
  "Budget Ospiti/Ciurma e grafici di controllo",
  "Un unico abbonamento per tutta l'imbarcazione",
  "Aggiornamenti e nuove funzioni inclusi",
];

const STEPS = [
  { n: "1", icon: Camera, title: "Fotografa", text: "Scatta una foto di fattura o scontrino. L'AI legge prodotti, quantità e prezzi." },
  { n: "2", icon: Calculator, title: "L'app calcola", text: "Food cost per coperto, budget Ospiti/Ciurma, giacenze e margini, in automatico." },
  { n: "3", icon: Send, title: "Invia il report", text: "PDF ed Excel professionali all'Armatore in un tocco, anche via email." },
];

const ROLES = [
  { icon: ChefHat, title: "Per lo Chef", text: "Registra le spese in pochi secondi, gestisce ricette, menù con allergeni e ordini ai fornitori." },
  { icon: Anchor, title: "Per il Comandante", text: "Tiene sotto controllo budget e food cost di bordo e charter, con avvisi e report sempre pronti." },
  { icon: Crown, title: "Per l'Armatore", text: "Riceve report chiari e trasparenti su spese e margini, in sola lettura, quando vuole." },
];

const FAQS = [
  { q: "Serve installare qualcosa?", a: "No. Funziona dal browser e si può aggiungere alla schermata Home come un'app. Funziona anche offline." },
  { q: "Posso usarla con tutto l'equipaggio?", a: "Sì: un unico abbonamento per tutta l'imbarcazione, senza costi per utente." },
  { q: "Come funziona la prova gratuita?", a: "30 giorni gratis, sempre inclusi. Nessun addebito durante la prova." },
  { q: "E se mi serve una funzione particolare?", a: "I miglioramenti su misura sono inclusi: li richiedi dallo Spazio Cliente, senza costi extra." },
  { q: "I miei dati sono al sicuro?", a: "Ogni imbarcazione ha il suo spazio dati riservato e i report sono accessibili solo a chi autorizzi." },
];

export default function Promo() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.origin + "/promo" : "https://yachtfoodcost.com/promo";
  const shareText = "YACHT FOOD COST — gestione cambusa e food cost per yacht. Guarda come funziona:";
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
  const tgHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("YACHT FOOD COST")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "YACHT FOOD COST", text: shareText, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  // Spazio cliente — codice + richiesta su misura
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [clientName, setClientName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqCategory, setReqCategory] = useState("Report");
  const [reqPriority, setReqPriority] = useState("media");
  const [reqDesc, setReqDesc] = useState("");
  const [reqSending, setReqSending] = useState(false);
  const [reqSent, setReqSent] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      navigate("/login");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) { toast.error("Inserisci il tuo codice"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/client/verify", { code: c });
      setClientName(data.client_name);
      toast.success(`Benvenuto, ${data.client_name}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Codice non valido");
    } finally { setVerifying(false); }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!reqEmail.trim() || !reqDesc.trim()) { toast.error("Inserisci email e descrizione"); return; }
    setReqSending(true);
    try {
      await api.post("/client/requests", {
        code: code.trim().toUpperCase(), email: reqEmail.trim(),
        category: reqCategory, priority: reqPriority, description: reqDesc.trim(),
      });
      setReqSent(true);
      toast.success("Richiesta inviata!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito");
    } finally { setReqSending(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Inserisci nome ed email");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", { name: name.trim(), email: email.trim() });
      setSent(true);
      toast.success("Richiesta inviata!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] font-sans text-navy" data-testid="promo-page">
      {/* NAV */}
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Yacht Food Cost" className="h-11 w-11 rounded-xl shadow-lg" />
            <span className="font-head text-lg font-bold tracking-tight text-white">YACHT FOOD COST</span>
          </div>
          <button data-testid="promo-login-cta" onClick={goLogin} className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-gold-hover">
            Accedi
          </button>
        </div>
      </header>

      {/* HERO + VIDEO — copertina */}
      <section className="relative overflow-hidden bg-navy-deep pb-16 pt-28 lg:pb-24 lg:pt-32">
        <img src={COVER} alt="Megayacht al tramonto" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy-deep/85 to-navy-deep" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 35%, rgba(6,15,30,0.78) 100%)" }} />
        <div className="animate-slide-up relative mx-auto w-full max-w-4xl px-5 text-center">
          <img src="/logo.png" alt="Yacht Food Cost" className="mx-auto h-20 w-20 rounded-2xl shadow-2xl ring-1 ring-gold/30" />
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-black/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-gold backdrop-blur">
            <Anchor className="h-3.5 w-3.5" /> Programma Beta 2026
          </span>
          <h1 className="mt-6 font-head text-5xl font-bold leading-[1.02] tracking-tight text-white drop-shadow-lg sm:text-6xl lg:text-7xl">
            Yacht <span className="text-gold">Food Cost</span>
          </h1>
          <p className="mt-5 font-head text-xl font-medium text-white/90 sm:text-2xl">Costruito a bordo, non in ufficio.</p>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Gestione cambusa e food cost pensata per chi vive a bordo. Guarda come funziona in due minuti.
          </p>

          <div className="mt-10 overflow-hidden rounded-2xl border border-gold/20 bg-navy shadow-2xl">
            <video data-testid="promo-video" controls preload="metadata" playsInline poster={COVER} className="h-auto w-full">
              <source src="/promo.mp4" type="video/mp4" />
              Il tuo browser non supporta la riproduzione del video.
            </video>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a data-testid="promo-download-video" href="/promo.mp4" download="yacht-food-cost.mp4" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-gold-hover">
              <Download className="h-4 w-4" /> Scarica il video
            </a>
            <a data-testid="promo-gallery-anchor" href="#schermate" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              <PlayCircle className="h-4 w-4" /> Sfoglia le schermate
            </a>
          </div>

          {/* SHARE BAR */}
          <div className="mt-8 flex flex-col items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Condividi con l'equipaggio</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a data-testid="promo-share-whatsapp" href={waHref} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:border-gold/50 hover:text-gold">
                <MessageCircle className="h-5 w-5" />
              </a>
              <a data-testid="promo-share-telegram" href={tgHref} target="_blank" rel="noopener noreferrer" title="Telegram" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:border-gold/50 hover:text-gold">
                <SendIcon className="h-5 w-5" />
              </a>
              <a data-testid="promo-share-email" href={mailHref} title="Email" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:border-gold/50 hover:text-gold">
                <Mail className="h-5 w-5" />
              </a>
              <button data-testid="promo-share-copy" onClick={copyLink} title="Copia link" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:border-gold/50 hover:text-gold">
                {copied ? <Check className="h-5 w-5 text-gold" /> : <Link2 className="h-5 w-5" />}
              </button>
              <button data-testid="promo-share-native" onClick={nativeShare} title="Condividi" className="flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 text-sm font-bold text-gold transition-colors hover:bg-gold/20">
                <Share2 className="h-4 w-4" /> Condividi
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Come funziona</p>
            <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Dalla fattura al report in 3 passi</h2>
          </div>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`step-${i}`} className="relative rounded-2xl border border-navy/10 bg-[#F9F7F2] p-7">
                <div className="absolute -top-4 left-7 flex h-9 w-9 items-center justify-center rounded-full bg-gold font-head text-sm font-bold text-black">{s.n}</div>
                <div className="mt-3 flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold"><s.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-head text-lg font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="schermate" className="mx-auto max-w-6xl px-5 py-20 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Le schermate del video</p>
          <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Ogni scena, un passo del lavoro a bordo</h2>
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {SHOTS.map((s, i) => (
            <figure
              key={s.src}
              data-testid={`promo-shot-${i + 1}`}
              className="group overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-lg transition-transform hover:-translate-y-1"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="overflow-hidden">
                <img src={s.src} alt={s.title} loading="lazy" className="aspect-[16/9] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <figcaption className="p-5">
                <div className="font-head text-xs font-semibold uppercase tracking-widest text-gold">{s.scene}</div>
                <div className="mt-1.5 font-head text-lg font-semibold text-navy">{s.title}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* VANTAGGI */}
      <section id="vantaggi" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Perché sceglierlo</p>
            <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Meno tempo sui conti, più controllo sul food cost</h2>
            <p className="mt-3 text-base text-slate-500">Pensato per chi vive la cambusa: veloce da bordo, chiaro per l'Armatore.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="rounded-2xl border border-navy/10 bg-[#F9F7F2] p-6 transition-transform hover:-translate-y-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold"><b.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-head text-lg font-semibold text-navy">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PER CHI È */}
      <section id="per-chi" className="bg-[#F4F1EA] py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Per tutto l'equipaggio</p>
            <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Un vantaggio per ogni ruolo a bordo</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {ROLES.map((r, i) => (
              <div key={i} data-testid={`role-${i}`} className="rounded-2xl border border-navy/10 bg-white p-7 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold"><r.icon className="h-7 w-7" /></div>
                <h3 className="mt-4 font-head text-lg font-semibold text-navy">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIANZA */}
      <section className="bg-navy py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <Quote className="mx-auto h-10 w-10 text-gold" />
          <p className="mt-6 font-head text-2xl font-medium leading-relaxed text-white sm:text-3xl">
            "L'ho pensata da chi sta in cambusa, non in ufficio: tutto quello che serve per tenere il food cost sotto controllo, veloce da usare anche nei giorni di charter."
          </p>
          <div className="mt-6 text-sm font-semibold uppercase tracking-widest text-gold">Tommaso · Chef &amp; fondatore</div>
        </div>
      </section>

      {/* PREZZI */}
      <section id="prezzi" className="bg-navy-deep py-20 lg:py-28">
        <div className="mx-auto max-w-lg px-5">
          <div className="text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Piano stagionale</p>
            <h2 className="mt-4 font-head text-3xl font-bold text-white sm:text-4xl">Tutto incluso, un unico abbonamento</h2>
            <p className="mt-3 text-sm text-slate-300">Un solo piano per tutta l'imbarcazione. Provalo gratis, decidi con calma.</p>
          </div>
          <div className="mt-10 overflow-hidden rounded-3xl border border-gold/30 bg-white shadow-2xl">
            <div className="bg-gradient-to-br from-navy to-navy-deep px-8 py-8 text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-gold"><Crown className="h-3.5 w-3.5" /> Stagione Completa</div>
              <div className="mt-3 inline-flex items-center rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-300">−25% Stagione inaugurale</div>
              <div className="mt-3 flex items-end justify-center gap-2 text-white">
                <span className="font-head text-2xl font-medium text-slate-400 line-through">€2.500</span>
                <span className="font-head text-5xl font-bold">€1.875</span>
                <span className="mb-1.5 text-slate-300">/ stagione</span>
              </div>
              <div className="mt-2 text-sm font-medium text-gold">Sempre 30 giorni di prova gratuita · poi €1.875 la prima stagione</div>
            </div>
            <div className="px-8 py-8">
              <ul className="space-y-3">
                {PLAN_INCLUDES.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-navy">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> {t}
                  </li>
                ))}
              </ul>
              <button data-testid="promo-checkout" onClick={startCheckout} disabled={checkoutLoading} className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-4 font-bold text-black transition-colors hover:bg-gold-hover disabled:opacity-60">
                {checkoutLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Inizia la prova gratuita
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">Nessun addebito durante la prova · Supporto sempre incluso · Accesso per tutto l'equipaggio</p>
            </div>
          </div>

          <div data-testid="compare-row" className="mt-6 rounded-2xl border border-gold/30 bg-white/5 p-5 text-center">
            <p className="text-sm leading-relaxed text-slate-200">
              <span className="font-semibold text-white">Con gli altri gestionali paghi extra per quasi tutto:</span> un costo per ogni utente, moduli aggiuntivi, personalizzazioni e assistenza — spesso da <span className="font-semibold text-white">€99 a €299 al mese</span>, più i costi nascosti.
            </p>
            <p className="mt-3 text-sm font-semibold leading-relaxed text-gold">
              Con Yacht Food Cost no: un unico prezzo stagionale, tutto incluso — miglioramenti su misura e supporto compresi, senza sorprese.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <div className="text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Domande frequenti</p>
            <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Tutto quello che vuoi sapere</h2>
          </div>
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`} className="rounded-2xl border border-navy/10 bg-[#F9F7F2] p-6">
                <h3 className="font-head text-base font-semibold text-navy">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPAZIO CLIENTE — richiesta su misura */}
      <section id="cliente" className="bg-[#F4F1EA] py-20 lg:py-28">
        <div className="mx-auto max-w-xl px-5">
          <div className="text-center">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Spazio Cliente</p>
            <h2 className="mt-3 font-head text-3xl font-bold text-navy sm:text-4xl">Chiedi una funzione su misura</h2>
            <p className="mt-2 text-sm text-slate-500">Hai un codice cliente? Inseriscilo e richiedi un miglioramento adatto alle tue esigenze, senza costi aggiuntivi.</p>
          </div>
          <div className="mt-8 rounded-2xl border border-navy/10 bg-white p-8 shadow-xl">
            {!clientName ? (
              <form onSubmit={verifyCode} className="space-y-4" data-testid="client-verify-form">
                <label className="block text-sm font-semibold text-navy">Il tuo codice cliente</label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="client-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="YFC-XXXXX" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 font-mono uppercase tracking-widest outline-none focus:border-gold" />
                </div>
                <button data-testid="client-verify-btn" type="submit" disabled={verifying} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy font-bold text-white transition-colors hover:bg-navy-hover disabled:opacity-60">
                  {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Continua
                </button>
              </form>
            ) : reqSent ? (
              <div data-testid="client-request-success" className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-5 font-medium text-emerald-700">
                <Check className="h-5 w-5 shrink-0" /> Grazie {clientName}! La tua richiesta è stata inviata. Ti ricontatteremo via email.
              </div>
            ) : (
              <form onSubmit={submitRequest} className="space-y-3" data-testid="client-request-form">
                <div className="flex items-center gap-2 rounded-lg bg-gold/10 px-4 py-3 text-sm text-navy">
                  <Sparkles className="h-4 w-4 text-gold" /> Ciao <b>{clientName}</b>, raccontaci cosa vuoi migliorare.
                </div>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="client-req-email" type="email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} placeholder="La tua email (per la risposta)" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Categoria</label>
                    <select data-testid="client-req-category" value={reqCategory} onChange={(e) => setReqCategory(e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold">
                      {["Report", "Menù", "Ordini", "Charter", "HACCP", "Acquisti", "Fornitori", "Altro"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Priorità</label>
                    <select data-testid="client-req-priority" value={reqPriority} onChange={(e) => setReqPriority(e.target.value)} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold">
                      <option value="bassa">Bassa</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                    </select>
                  </div>
                </div>
                <textarea data-testid="client-req-desc" value={reqDesc} onChange={(e) => setReqDesc(e.target.value)} rows={4} placeholder="Descrivi la funzione o il miglioramento che vorresti…" className="w-full rounded-lg border-2 border-slate-200 p-3 outline-none focus:border-gold" />
                <button data-testid="client-req-submit" type="submit" disabled={reqSending} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy font-bold text-white transition-colors hover:bg-navy-hover disabled:opacity-60">
                  {reqSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Invia la richiesta
                </button>
                <button type="button" onClick={() => { setClientName(""); setCode(""); }} className="w-full text-center text-xs font-semibold text-slate-400 hover:text-navy">
                  Cambia codice
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* RICHIEDI ACCESSO */}
      <section id="accesso" className="bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-xl px-5">
          <div className="rounded-2xl border border-white/10 bg-white p-8 shadow-xl">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Programma Beta 2026</p>
            <h2 className="mt-3 font-head text-3xl font-bold text-navy">Richiedi l'accesso</h2>
            <p className="mt-2 text-sm text-slate-500">Lascia nome ed email: ti avvisiamo appena Yacht Food Cost è pronto per te.</p>
            {sent ? (
              <div data-testid="promo-lead-success" className="mt-6 flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-4 font-medium text-emerald-700">
                <Check className="h-5 w-5" /> Grazie! Ti contatteremo presto.
              </div>
            ) : (
              <form onSubmit={submitLead} className="mt-6 space-y-3">
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="promo-lead-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
                </div>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="promo-lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
                </div>
                <button data-testid="promo-lead-submit" type="submit" disabled={sending} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy font-bold text-white transition-colors hover:bg-navy-hover disabled:opacity-60">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Richiedi l'accesso
                </button>
              </form>
            )}
            <button data-testid="promo-goto-login" onClick={goLogin} className="mt-4 flex w-full items-center justify-center gap-2 text-sm font-semibold text-navy hover:text-gold">
              Hai già un accesso? Entra nell'app <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-navy-deep py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <h2 className="font-head text-3xl font-bold text-white sm:text-4xl">Pronto a tenere il food cost sotto controllo?</h2>
          <p className="mt-4 text-base text-slate-300">Provalo gratis per 30 giorni. Nessun addebito, supporto sempre incluso.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="cta-final-checkout" onClick={startCheckout} disabled={checkoutLoading} className="flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-bold text-black transition-colors hover:bg-gold-hover disabled:opacity-60">
              {checkoutLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Inizia la prova gratuita
            </button>
            <a href="#accesso" className="rounded-full border border-white/40 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10">Richiedi accesso</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-deep py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Yacht Food Cost" className="h-8 w-8 rounded-lg" />
            <span className="font-head font-semibold text-white">YACHT FOOD COST</span>
          </div>
          <span>yachtfoodcost.com · Per chi sta in cambusa</span>
        </div>
      </footer>
    </div>
  );
}
